import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { chatAPI } from '../services/apiService';
import { useTheme } from '../theme/ThemeContext';

interface LoginFormProps {
  onLoginSuccess: () => void;
}

export default function LoginForm({ onLoginSuccess }: LoginFormProps) {
  const { mode } = useTheme();
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Allowed email domains for registration
  const allowedDomains = ['@lexhr.ai', '@hradvisor.ai', '@dataexpert.hu','@alivehr.co.uk','@adviserplus.com', '@empoweringpeoplegroup.com', '@rubiklab.ai', '@kingstrust.org.uk', '@aston.ac.uk', '@cryptio.co'];

  const validateEmailDomain = (email: string): boolean => {
    return allowedDomains.some(domain => email.toLowerCase().endsWith(domain.toLowerCase()));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Please fill in all fields');
      return;
    }

    // Validate email domain for registration
    if (!isLogin && !validateEmailDomain(username)) {
      setError(`You are not allowed to register with this email domain. Please contact support if you believe this is an error.`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (isLogin) {
        await chatAPI.login(username, password);
      } else {
        await chatAPI.register(username, password);
      }
      onLoginSuccess();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen theme-bg-secondary flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="theme-bg rounded-2xl shadow-xl p-8 w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.3, ease: "easeOut" }}
            className="h-10 mx-auto mb-4 flex items-center justify-center"
          >
            <img 
              src={mode === 'dark' ? '/lexdark.webp' : '/lex.webp'} 
              alt="LexHR Logo" 
              className="w-full h-full object-contain"
            />
          </motion.div>
          <motion.p 
            key={isLogin ? 'login' : 'register'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="theme-text-secondary"
          >
            {isLogin ? 'Sign in to continue' : 'Create your account'}
          </motion.p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium theme-text mb-2">
              E-mail address
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border theme-border rounded-lg focus:ring-2 focus:ring-primary focus:ring-opacity-50 focus:border-transparent transition-colors theme-bg-secondary theme-text placeholder:theme-text-muted"
              placeholder="Enter your e-mail address"
              disabled={isLoading}
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium theme-text mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border theme-border rounded-lg focus:ring-2 focus:ring-primary focus:ring-opacity-50 focus:border-transparent transition-colors theme-bg-secondary theme-text placeholder:theme-text-muted"
              placeholder="Enter your password"
              disabled={isLoading}
            />
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="theme-bg-secondary border theme-border theme-error px-4 py-3 rounded-lg text-sm"
            >
              {error}
            </motion.div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full theme-primary theme-text-inverse py-3 px-4 rounded-lg font-medium theme-primary-hover focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 theme-text-inverse" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isLogin ? 'Signing in...' : 'Creating account...'}
              </div>
            ) : (
              <motion.span
                key={isLogin ? 'signin' : 'createaccount'}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
              >
                {isLogin ? 'Sign In' : 'Create Account'}
              </motion.span>
            )}
          </button>

          {/* Toggle Mode */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="theme-primary-text hover:opacity-80 text-sm font-medium transition-colors"
              disabled={isLoading}
            >
              <motion.span
                key={isLogin ? 'toggle-register' : 'toggle-login'}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
              >
                {isLogin 
                  ? "Don't have an account? Create one" 
                  : "Already have an account? Sign in"
                }
              </motion.span>
            </button>
          </div>
        </form>

  
      </motion.div>
    </div>
  );
}
